import React, { useEffect, useState } from 'react';
import { partnerApiService } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import {
  CODE_ALREADY_REGISTERED,
  CODE_PARTNER_NOT_FOUND,
  apiErrorCode,
  apiErrorMessage,
} from '../../services/apiErrors';
import { formatPhone } from '../../utils/phone';
import { AuthMode } from './PhoneStepForm';
import { Notice, Spinner } from './AuthParts';
import './auth.css';

interface CodeStepFormProps {
  mode: AuthMode;
  phone: string;
  /** Вход — в кабинет, регистрация — в анкету. */
  onSuccess: () => void;
  onBack: () => void;
  onWrongDoor: (phone: string, message: string) => void;
}

const RESEND_DELAY_SECONDS = 60;
const CODE_LENGTH = 4;

/**
 * Шаг 2 обеих дверей: код из WhatsApp.
 *
 * Вход после проверки кода открывает кабинет. Регистрация — только выдаёт
 * токен: заведения ещё нет, и следующий экран не кабинет, а анкета. Пока
 * она не отправлена, все маршруты кабинета отвечают `registration_incomplete`.
 */
export const CodeStepForm: React.FC<CodeStepFormProps> = ({
  mode,
  phone,
  onSuccess,
  onBack,
  onWrongDoor,
}) => {
  const [code, setCode] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [timeLeft, setTimeLeft] = useState(RESEND_DELAY_SECONDS);
  const { login, beginRegistration } = useAuth();
  const { t } = useLanguage();

  const isRegistration = mode === 'registration';

  useEffect(() => {
    if (timeLeft <= 0) return;
    const timer = setTimeout(() => setTimeLeft((seconds) => seconds - 1), 1000);
    return () => clearTimeout(timer);
  }, [timeLeft]);

  const handleWrongDoor = (err: unknown): boolean => {
    const code409or404 = apiErrorCode(err);
    const expected = isRegistration ? CODE_ALREADY_REGISTERED : CODE_PARTNER_NOT_FOUND;
    if (code409or404 !== expected) return false;

    onWrongDoor(phone, apiErrorMessage(err));
    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    try {
      if (isRegistration) {
        const response = await partnerApiService.verifyRegistration({ phone, code });
        // Токен сохраняем сразу: анкета уходит уже под ним. Кабинет при этом
        // остаётся закрытым — заведения ещё нет.
        beginRegistration(response.user, response.token, phone);
      } else {
        const response = await partnerApiService.verifyLogin({ phone, code });
        login(response.user, response.partner, response.token);
      }
      onSuccess();
    } catch (err) {
      if (!handleWrongDoor(err)) {
        setError(apiErrorMessage(err));
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendCode = async () => {
    setIsLoading(true);
    setError('');

    try {
      if (isRegistration) {
        await partnerApiService.sendRegistrationCode({ phone });
      } else {
        await partnerApiService.sendLoginCode({ phone });
      }
      setTimeLeft(RESEND_DELAY_SECONDS);
      setCode('');
    } catch (err) {
      if (!handleWrongDoor(err)) {
        setError(apiErrorMessage(err));
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="m3-auth-screen">
      <div className="m3-auth-card">
        <button type="button" onClick={onBack} className="m3-btn-text qp-auth-back">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden>
            <path d="M15 5l-7 7 7 7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
          {t.common.back}
        </button>

        <h1 className="m3-headline">{t.auth.enterCode}</h1>
        <p className="m3-body" style={{ marginBottom: 28 }}>
          {t.auth.codeSentTo} {formatPhone(phone)}
        </p>

        {isRegistration && (
          <Notice>{t.auth.codeNoticeRegistration}</Notice>
        )}

        <form onSubmit={handleSubmit} className="qp-auth-form">
          <div className="qp-auth-control">
            <label className={`m3-field${error ? ' m3-field--error' : ''}`} htmlFor="code">
              <input
                id="code"
                name="code"
                type="text"
                inputMode="numeric"
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, CODE_LENGTH))}
                placeholder="0000"
                className="m3-field-input qp-auth-code-input"
                required
                disabled={isLoading}
                maxLength={CODE_LENGTH}
                autoComplete="one-time-code"
                aria-invalid={error ? true : undefined}
              />
              <span className="m3-field-label">{t.auth.codeLabel}</span>
            </label>
            {error && (
              <span className="m3-field-support m3-field-support--error" role="alert">
                {error}
              </span>
            )}
          </div>

          <button
            type="submit"
            disabled={isLoading || code.length !== CODE_LENGTH}
            className="m3-btn-filled m3-btn-filled--block"
          >
            {isLoading && <Spinner />}
            {isLoading ? t.auth.codeVerifying : isRegistration ? t.auth.codeConfirm : t.auth.otherDoorLogin}
          </button>
        </form>

        <div className="qp-auth-resend">
          {timeLeft <= 0 ? (
            <button type="button" onClick={handleResendCode} disabled={isLoading} className="m3-btn-text">
              {t.auth.codeResend}
            </button>
          ) : (
            <p className="m3-body" style={{ fontSize: 13 }}>
              {t.auth.codeResendIn} {timeLeft} {t.auth.secondsShort}
            </p>
          )}
        </div>
      </div>
    </div>
  );
};
